'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Play } from 'lucide-react';

const isVideoUrl = (url?: string) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

interface VideoBannerProps {
    data?: {
        title?: string | null;
        buttonContent?: string | null;
        link?: string | null;
        image_info?: { url: string; description?: string } | null;
    };
}

export default function VideoBanner({ data }: VideoBannerProps) {
    if (!data || !data.image_info?.url) return null;

    const isVideo = isVideoUrl(data.image_info.url);

    const content = (
        <div className="relative w-full h-[70vh] overflow-hidden">
            {isVideo ? (
                <video
                    src={data.image_info.url}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : (
                <Image
                    src={data.image_info.url}
                    alt={data.image_info.description || data.title || ''}
                    fill
                    className="object-cover"
                />
            )}

            <div className="absolute inset-0 bg-black/70" />

            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-primary-0 text-center">
                {data.title && (
                    <h2
                        dir="ltr"
                        className="text-4xl md:text-6xl tracking-[0.45em] uppercase font-light -mr-[0.35em]"
                    >
                        {data.title}
                    </h2>
                )}
                {data.buttonContent && (
                    <span className="flex items-center gap-2 border border-primary-0 rounded-full px-5 py-2 backdrop-blur-sm bg-black/20">
                        <span className="flex items-center justify-center rounded-full bg-primary-0/90 size-8">
                            <Play className="size-4 text-black" />
                        </span>
                        {data.buttonContent}
                    </span>
                )}
            </div>
        </div>
    );

    return data.link ? <Link href={data.link}>{content}</Link> : content;
}